import { InputHTMLAttributes, ReactNode, FocusEvent, useEffect, useMemo, useState } from 'react'

import { DatePickerDay } from './DatePickerDay.tsx'
import { DatePickerHeading } from './DatePickerHeading.tsx'
import {
    Button,
    Dropdown,
    FormControl,
    FormControlProps,
    IconCalendar,
    IconCalendarDate,
    IconClose
} from '@/components'
import { DateRangeType, FormChangeEventHandler } from '@/types'

interface Props extends FormControlProps, Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
    value: Date | DateRangeType | null
    onChange: FormChangeEventHandler
    singleMode?: boolean
    maxDate?: Date
    minDate?: Date
    clearable?: boolean
}
const isSingleModeFunc = (value: Date | DateRangeType | null): value is Date =>
    !Array.isArray(value)
const isRangeModeFunc = (value: Date | DateRangeType | null): value is DateRangeType =>
    Array.isArray(value)

export const DatePicker = ({
    id,
    label,
    errors = [],
    warnings = [],
    hint,
    preIcon,
    postIcon,
    clearable,
    value,
    transparent,
    tooltip,
    singleMode = true,
    maxDate,
    minDate,
    className,
    'input-size': size,
    'data-test': dataTest,
    ...props
}: Props) => {
    dataTest = dataTest || id
    const isSingleMode = isSingleModeFunc(value)
    const isRangeMode = isRangeModeFunc(value)

    const curValueSingle = isSingleMode && value ? value : new Date()
    const curValueRange = isRangeMode && value[0] ? value[0] : new Date()
    const [currentDate, setCurrentDate] = useState((isSingleMode ? curValueSingle : curValueRange).startOf('month'))
    const [tempValue, setTempValue] = useState<Date | null>(null)
    const [hoveredValue, setHoveredValue] = useState<Date | null>(null)
    const [isOpen, setIsOpen] = useState(false)
    const [displayValue, setDisplayValue] = useState('')

    const toDisplayValue = (value: Date | DateRangeType | null) => {
        if (isSingleMode) {
            if (value && value.toString() !== 'Invalid Date') {
                return (value as Date).format()
            }
        } else {
            if ((value as DateRangeType)[0] && (value as DateRangeType)[1]) {
                const start = (value as DateRangeType)[0] as Date
                const end = (value as DateRangeType)[1] as Date
                return `${start.format()} - ${end.format()}`
            }
        }
        return ''
    }

    const handleChange = (value: Date | DateRangeType | null) => {
        props.onChange({
            target: {
                name: props.name as string,
                value,
                displayValue: toDisplayValue(value)
            }
        })
    }

    const handleClick = (value: Date) => {
        if (singleMode) {
            handleChange(value)
            setIsOpen(false)
        } else {
            if (tempValue) {
                handleChange([tempValue, value].sort((a, b) => +a - +b) as DateRangeType)
                setTempValue(null)
                setIsOpen(false)
            } else {
                setTempValue(value)
            }
        }
    }

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        if (isSingleMode) {
            const newVal = new Date(e.target.value)
            if (newVal.isValid()) {
                handleChange(newVal)
            } else {
                setDisplayValue(toDisplayValue(value))
            }
        }
        if (isRangeMode) {
            const inputValue = e.target.value.split(' - ')
            const start = inputValue[0] ? new Date(inputValue[0]) : null
            const end = inputValue[0] ? new Date(inputValue[1]) : null
            if (start?.isValid() && end?.isValid()) {
                if (!start.isEqualTo(value[0]) || !end.isEqualTo(value[1])) {
                    handleChange([start, end].sort((a, b) => +a - +b) as DateRangeType)
                }
            } else {
                setDisplayValue(toDisplayValue(value))
            }
        }
    }

    useEffect(() => {
        setDisplayValue(toDisplayValue(value))
    }, [value, singleMode])

    useEffect(() => {
        if ((isSingleMode && value) || (isRangeMode && value[0] && value[1])) {
            handleChange(value)
        }
    }, [])

    const postIconMemo = useMemo((): ReactNode => {
        if (clearable) {
            const rangeValue = value as DateRangeType
            if ((singleMode && value) || (!singleMode && (rangeValue[0] || rangeValue[1]))) {
                return <Button
                    data-test={`${dataTest}-clear-icon`}
                    design="btn-link-gray"
                    onClick={() => {
                        handleChange(isSingleMode ? null : [null, null])
                    }}
                >
                    <IconClose className="stroke-gray-500"/>
                </Button>
            }
            return postIcon
        }
        return <IconCalendarDate className="stroke-gray-500"/>
    }, [value, singleMode, dataTest, clearable, props.name])

    const calendar = useMemo(() => {
        const arr: Date[][] = []
        const start = currentDate.clone().startOf('month').startOf('week')
        const end = currentDate.clone().endOf('month').endOf('week')
        do {
            const weekEnd = start.clone().endOf('week')
            const row: Date[] = []
            do {
                row.push(start.clone())
                start.add('day', 1)
            } while (start.isBefore(weekEnd))
            arr.push(row)
        } while (start.isBefore(end))
        return arr
    }, [currentDate])

    return <FormControl
        id={id}
        label={label}
        data-test={dataTest}
        preIcon={clearable ? <IconCalendar className="stroke-gray-500"/> : preIcon}
        postIcon={postIconMemo}
        errors={errors}
        warnings={warnings}
        input-size={size}
        transparent={transparent}
        tooltip={tooltip}
        hint={hint}
        className={className}
    >
        <Dropdown
            reference={<input
                id={id}
                autoComplete="off"
                data-test={`${dataTest}${dataTest ? '-' : ''}input`}
                {...props}
                value={displayValue}
                onChange={e => {
                    setDisplayValue(e.target.value)
                }}
                onBlur={handleBlur}
            />}
            persistent={true}
            onChange={({ open }) => setIsOpen(open)}
            isOpen={isOpen}
        >
            <div className="flex flex-col gap-2 text-gray-700 text-sm">
                <DatePickerHeading
                    id={id}
                    currentDate={currentDate}
                    onChange={e => setCurrentDate(e.startOf('month'))}
                    maxDate={maxDate}
                    minDate={minDate}
                    data-test={dataTest}
                />
                <div className="grid grid-cols-7">
                    {calendar.map(row => row.map(item => <DatePickerDay
                        value={value}
                        tempValue={tempValue}
                        hoveredValue={hoveredValue}
                        day={item}
                        key={item.toISODate()}
                        currentDate={currentDate}
                        onClick={handleClick}
                        maxDate={maxDate}
                        minDate={minDate}
                        data-test={dataTest}
                        onMouseEnter={() => setHoveredValue(item)}
                        onMouseLeave={() => setHoveredValue(null)}
                    />))}
                </div>
            </div>
        </Dropdown>
    </FormControl>
}
